/**
 * Ordering the file tree.
 *
 * WHY THIS MODULE EXISTS
 *   tree.ts builds exactly ONE order — folders first, then natural name —
 *   because that is the order a file browser should open in. But an engineer
 *   back from two weeks of leave wants "what moved most recently" at the top,
 *   and one chasing a slow sync wants the 400 MB assembly at the top. Those
 *   are the same tree read through a different comparator, so the comparator
 *   is the thing worth naming and testing. Everything here is a pure function
 *   over an ALREADY-BUILT tree: sorting never re-folds the change log, so
 *   changing the order costs a sort, not a rebuild.
 *
 * DATA DEFINITIONS
 *
 *   A SortKey is one of "name" | "modified" | "created" | "size" | "kind".
 *   Interpretation: which property of a node decides the order. "modified"
 *   and "created" are instants, "size" is bytes, "kind" is the engineering
 *   role from filekind.ts (parts before drawings before archives), and "name"
 *   is the natural, digit-aware name order — "Part9" before "Part10".
 *
 *   A SortDirection is "asc" | "desc".
 *   Interpretation: which end of the key leads. It reverses the KEY only; it
 *   never reverses the folders-first grouping and never reverses where nodes
 *   with no value for the key land (see below).
 *
 *   A SortOrder is { key, direction }.
 *   Interpretation: one complete answer to "how is this tree listed right
 *   now". It is the unit the UI stores and the user chooses.
 *
 * WHAT A FOLDER'S DATE AND SIZE MEAN
 *   A folder was never written to disk. It exists only because files inside
 *   it do (see tree.ts), so it has no date and no size of its own. We
 *   therefore read a folder as the SUMMARY of everything below it:
 *     - modified: the most recent updatedAt among its descendants, which
 *       FolderNode already rolls up. A folder changed when anything in it
 *       changed — that is what someone scanning for recent work means.
 *     - created:  the EARLIEST createdAt among its descendants: the folder
 *       came into being with its first file. FolderNode does not carry this
 *       (nothing else has needed it), so it is computed here and cached.
 *     - size:     the total bytes below it, which FolderNode also rolls up.
 *     - kind:     nothing at all. A folder has no engineering role. Folders
 *       are grouped above files anyway, so folder-against-folder falls back
 *       to name order.
 */
import {} from './tree.js';
import { compareNames } from './path.js';
import { KIND_ORDER, kindOf } from './filekind.js';
/** Every key, in the order a menu should offer them. */
export const SORT_KEYS = ['name', 'modified', 'created', 'size', 'kind'];
/**
 * How the tree lists when nobody has chosen anything.
 * Name-ascending, i.e. exactly what buildTree already produces, so a first
 * visit looks the way it always has.
 */
export const DEFAULT_SORT = { key: 'name', direction: 'asc' };
/** True when an arbitrary string is one of our keys. isSortKey : String -> Boolean */
export const isSortKey = (s) => typeof s === 'string' && SORT_KEYS.includes(s);
/**
 * The direction a key is most useful in the moment you pick it.
 * Dates and sizes are asked as superlatives — "what is newest", "what is
 * biggest" — so they open descending. Names and kinds are an index you scan,
 * so they open A→Z.
 * defaultDirectionFor : SortKey -> SortDirection
 */
export const defaultDirectionFor = (key) => key === 'name' || key === 'kind' ? 'asc' : 'desc';
/* ------------------------------------------------------------------ */
/* Reading a value out of a node                                       */
/* ------------------------------------------------------------------ */
/**
 * When this node last changed, as an ISO timestamp, or "" when unknown.
 * For a folder this is the newest change anywhere below it.
 * modifiedAtOf : TreeNode -> String
 */
export const modifiedAtOf = (node) => node.type === 'file' ? node.entry.updatedAt : node.updatedAt;
/**
 * Total bytes of this node. A folder weighs what its descendants weigh.
 * sizeOf : TreeNode -> Number
 */
export const sizeOf = (node) => node.type === 'file' ? node.entry.size : node.totalSize;
/**
 * The oldest createdAt below a folder is cached per folder OBJECT, because
 * during one sort the same folder is compared against each of its siblings
 * and an uncached walk would re-read the whole subtree every time. A WeakMap
 * keeps nothing alive: the trees are rebuilt from the log on every sync and
 * the old ones are collected with their cache entries.
 */
const earliestCreated = new WeakMap();
/**
 * When this node came into existence, as an ISO timestamp, or "" when
 * unknown. A folder was created when the first file inside it was.
 * createdAtOf : TreeNode -> String
 */
export function createdAtOf(node) {
    if (node.type === 'file')
        return node.entry.createdAt;
    const cached = earliestCreated.get(node);
    if (cached !== undefined)
        return cached;
    let earliest = '';
    for (const child of node.children) {
        const at = createdAtOf(child);
        if (at === '')
            continue;
        if (earliest === '' || compareTimestamps(at, earliest) < 0)
            earliest = at;
    }
    earliestCreated.set(node, earliest);
    return earliest;
}
const KIND_RANK = new Map(KIND_ORDER.map((k, i) => [k, i]));
/**
 * Compare two ISO timestamps as INSTANTS rather than as text.
 * The log carries real offsets — "+05:30" from the engineers' workstations,
 * "Z" from the server — and "2026-08-01T10:00:00+05:30" is four and a half
 * hours EARLIER than "2026-08-01T06:00:00Z" although it sorts later as a
 * string. Anything unparseable falls back to text so the order stays total.
 * compareTimestamps : String String -> Number
 */
export function compareTimestamps(a, b) {
    const ta = Date.parse(a);
    const tb = Date.parse(b);
    if (Number.isNaN(ta) || Number.isNaN(tb))
        return a < b ? -1 : a > b ? 1 : 0;
    return ta - tb;
}
/** Does this node carry a real value for this key? hasValue : TreeNode SortKey -> Boolean */
function hasValue(node, key) {
    if (key === 'modified')
        return modifiedAtOf(node) !== '';
    if (key === 'created')
        return createdAtOf(node) !== '';
    // A size of zero and a kind of "other" are answers, not absences.
    return true;
}
/** The node's value for the key, compared ascending. compareByKey : TreeNode TreeNode SortKey -> Number */
function compareByKey(a, b, key) {
    switch (key) {
        case 'name': return compareNames(a.name, b.name);
        case 'modified': return compareTimestamps(modifiedAtOf(a), modifiedAtOf(b));
        case 'created': return compareTimestamps(createdAtOf(a), createdAtOf(b));
        case 'size': return sizeOf(a) - sizeOf(b);
        case 'kind': return compareKinds(a, b);
    }
}
/**
 * Engineering role order, from filekind.ts: parts, assemblies, drawings…
 * Two folders are equal here — a folder has no role — which leaves them to
 * the name tie-break below.
 */
function compareKinds(a, b) {
    if (a.type === 'folder' || b.type === 'folder')
        return 0;
    return (KIND_RANK.get(kindOf(a.path)) ?? KIND_ORDER.length) -
        (KIND_RANK.get(kindOf(b.path)) ?? KIND_ORDER.length);
}
/* ------------------------------------------------------------------ */
/* The comparator                                                      */
/* ------------------------------------------------------------------ */
/**
 * Order two siblings under one SortOrder.
 *
 * This is the whole policy in one place, and the order of its three rules is
 * the policy:
 *   1. Folders above files, whatever the key and whatever the direction. That
 *      is what every file browser does; a "largest first" list that buries
 *      the folders at the bottom is one nobody can scan.
 *   2. Nodes with no value for the key sink to the bottom in BOTH directions.
 *      A missing date says nothing, so it should not lead the list the moment
 *      you reverse it.
 *   3. The key itself, reversed for "desc"; then ties break by natural name
 *      ASCENDING in both directions, because two files saved in the same
 *      second are tied about time, not about name, and A→Z is the order a
 *      person can predict. Identical names (which differ only in case, since
 *      paths collide case-insensitively) fall back to the raw path so the
 *      order is total and a re-sort never reshuffles.
 *
 * compareNodes : TreeNode TreeNode SortOrder -> Number
 */
export function compareNodes(a, b, order) {
    if (a.type !== b.type)
        return a.type === 'folder' ? -1 : 1;
    const aHas = hasValue(a, order.key);
    const bHas = hasValue(b, order.key);
    if (aHas !== bHas)
        return aHas ? -1 : 1;
    const primary = compareByKey(a, b, order.key);
    const directed = order.direction === 'desc' ? -primary : primary;
    if (directed !== 0)
        return directed;
    const byName = compareNames(a.name, b.name);
    if (byName !== 0)
        return byName;
    return a.path < b.path ? -1 : a.path > b.path ? 1 : 0;
}
/**
 * The same tree, listed under a different order, at every depth.
 *
 * Returns new arrays and new FolderNodes rather than sorting in place: the
 * tree the caller passed in is shared (it is memoised per project and read by
 * other views), and mutating it would reorder those views behind their backs.
 * The FileNodes themselves are reused — they are immutable.
 *
 * sortTree : [TreeNode] SortOrder -> [TreeNode]
 */
export function sortTree(nodes, order) {
    const deep = nodes.map(node => node.type === 'folder' ? { ...node, children: sortTree(node.children, order) } : node);
    return deep.sort((a, b) => compareNodes(a, b, order));
}
/* ------------------------------------------------------------------ */
/* Storage                                                             */
/* ------------------------------------------------------------------ */
/**
 * A SortOrder as one short string, for localStorage: "modified:desc".
 * formatSortOrder : SortOrder -> String
 */
export const formatSortOrder = (order) => `${order.key}:${order.direction}`;
/**
 * Read a SortOrder back, tolerating anything at all.
 * What comes out of storage was written by an older build, or hand-edited, or
 * is simply null because this browser has never seen this project — so this
 * is a total function and every unrecognised shape means "the default".
 * parseSortOrder : Unknown -> SortOrder
 */
export function parseSortOrder(raw) {
    if (typeof raw !== 'string')
        return DEFAULT_SORT;
    const [key, direction] = raw.split(':');
    if (!isSortKey(key))
        return DEFAULT_SORT;
    if (direction !== 'asc' && direction !== 'desc')
        return { key, direction: defaultDirectionFor(key) };
    return { key, direction };
}
//# sourceMappingURL=sort.js.map